import React from 'react';
import { Box, Container, Typography, Button, Grid, Card, CardContent } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// Icons
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import WebIcon from '@mui/icons-material/Web';
import SportsBaseballIcon from '@mui/icons-material/SportsBaseball';
import CampaignIcon from '@mui/icons-material/Campaign';
import PaymentIcon from '@mui/icons-material/Payment';

// Components
import AnimatedHero from '../components/ui/AnimatedHero';
import PartnersCarousel from '../components/ui/PartnersCarousel';

const MotionBox = motion(Box);
const MotionTypography = motion(Typography);

const HomePage = () => {
  const { t } = useTranslation();

  const services = [
    {
      id: 'ivr',
      title: t('services.ivr.title'),
      description: t('services.ivr.description'),
      icon: <PhoneInTalkIcon fontSize="large" />,
      color: '#2563eb',
    },
    {
      id: 'webdev',
      title: t('services.webDev.title'),
      description: t('services.webDev.description'),
      icon: <WebIcon fontSize="large" />,
      color: '#0ea5e9',
    },
    {
      id: 'sports',
      title: t('services.sports.title'),
      description: t('services.sports.description'),
      icon: <SportsBaseballIcon fontSize="large" />,
      color: '#f59e0b',
    },
    {
      id: 'communication',
      title: t('services.communication.title'),
      description: t('services.communication.description'),
      icon: <CampaignIcon fontSize="large" />,
      color: '#2563eb',
    },
    {
      id: 'automation',
      title: t('services.automation.title'),
      description: t('services.automation.description'),
      icon: <PaymentIcon fontSize="large" />,
      color: '#0ea5e9',
    },
  ];

  return (
    <>
      {/* Epic Animated Hero Section */}
      <AnimatedHero />

      {/* Services Grid Section */}
      <Box
        component="section"
        sx={{
          py: { xs: 8, md: 12 },
          background: 'var(--bg-primary)',
          position: 'relative',
        }}
      >
        <Container maxWidth="lg">
          {/* Section Header */}
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <MotionTypography
              component="h2"
              variant="h3"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              sx={{
                fontSize: { xs: '1.8rem', md: '2.5rem' },
                fontWeight: 700,
                color: 'var(--text-primary)',
                mb: 2,
              }}
            >
              {t('services.title')}
            </MotionTypography>
            <MotionBox
              initial={{ opacity: 0, scaleX: 0 }}
              whileInView={{ opacity: 1, scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2, ease: 'easeOut' }}
              sx={{
                height: '3px',
                width: '60px',
                background: 'var(--gradient-primary)',
                borderRadius: 'var(--radius-full)',
                mx: 'auto',
                mb: 2,
              }}
            />
            <Typography
              variant="h6"
              sx={{
                color: 'var(--text-secondary)',
                fontWeight: 400,
                fontSize: '1rem',
              }}
            >
              {t('services.description')}
            </Typography>
          </Box>

          {/* Services Grid */}
          <Grid container spacing={3}>
            {services.map((service, index) => (
              <Grid item key={service.id} xs={12} sm={6} md={4}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.06, ease: 'easeOut' }}
                  style={{ height: '100%' }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      background: 'var(--card-bg)',
                      border: '1px solid var(--border-color)',
                      borderRadius: 'var(--radius-xl)',
                      transition: 'all var(--transition-base)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: service.color,
                        transition: 'all var(--transition-base)',
                      },
                      '&:hover': {
                        borderColor: service.color,
                        boxShadow: `0 12px 24px rgba(15, 20, 25, 0.1)`,
                        transform: 'translateY(-4px)',
                        '&::before': {
                          height: '4px',
                        },
                      },
                    }}
                  >
                    {/* Icon Container */}
                    <Box
                      sx={{
                        p: 3,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        background: `linear-gradient(135deg, ${service.color}15 0%, ${service.color}08 100%)`,
                        color: service.color,
                      }}
                    >
                      <Box sx={{ fontSize: '2.5rem', display: 'flex' }}>
                        {service.icon}
                      </Box>
                    </Box>

                    {/* Content */}
                    <CardContent sx={{ flexGrow: 1, pb: 2 }}>
                      <Typography
                        component="h3"
                        variant="h6"
                        sx={{
                          mb: 1,
                          fontWeight: 600,
                          color: 'var(--text-primary)',
                          fontSize: '1.1rem',
                        }}
                      >
                        {service.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'var(--text-secondary)',
                          lineHeight: 1.6,
                        }}
                      >
                        {service.description}
                      </Typography>
                    </CardContent>

                    {/* Footer Action */}
                    <Box sx={{ px: 3, pb: 2 }}>
                      <Button
                        size="small"
                        component={RouterLink}
                        to={`/services#${service.id}`}
                        sx={{
                          color: service.color,
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '0.9rem',
                          transition: 'all var(--transition-fast)',
                          '&:hover': {
                            backgroundColor: `${service.color}15`,
                          },
                        }}
                        endIcon={<ArrowForwardIcon sx={{ fontSize: '1rem' }} />}
                      >
                        {t('services.learnMore')}
                      </Button>
                    </Box>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Partners Carousel Section */}
      <PartnersCarousel />
    </>
  );
};

export default HomePage;
